/**
 * Network — UI comparison view (Phase 3 / JAISIU-1653).
 *
 * Side-by-side diff of two or more selected instances, with a checkbox-style
 * match/diff/missing indicator and the same rationale text the CLI uses.
 *
 * Reuses the diff engine from src/network/diff.ts so the markdown output of
 * `jaisiu network compare` and the UI stay in sync.
 */

import { html, nothing } from "lit";
import type { InstanceComparison, InstanceRecord } from "../../../../src/network/types.js";
import { compareRecords } from "../../../../src/network/diff-pure.js";

export type NetworkInstance = InstanceRecord & { status: "online" | "degraded" | "offline" };

export type NetworkCompareProps = {
  instances: NetworkInstance[];
  ids: string[];
  loading?: boolean;
  error?: string | null;
  onClose?: () => void;
  onRemove?: (id: string) => void;
};

export function renderNetworkCompare(props: NetworkCompareProps) {
  if (props.ids.length < 2) {
    return html`
      <section class="card">
        <div class="card-title">Compare instances</div>
        <div class="muted">Select at least two instances from the list to compare.</div>
      </section>
    `;
  }
  if (props.loading) {
    return html`
      <section class="card">
        <div class="muted">Loading comparison…</div>
      </section>
    `;
  }
  if (props.error) {
    return html`
      <section class="card">
        <div class="callout danger">${props.error}</div>
      </section>
    `;
  }
  const selected = props.instances.filter((i) => props.ids.includes(i.id));
  const cmp: InstanceComparison = compareRecords(selected);
  return html`
    <section class="card" data-network-compare>
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Compare ${cmp.ids.length} instances</div>
          <div class="card-sub">${cmp.differences.length} difference(s)</div>
        </div>
        ${
          props.onClose ? html`<button class="btn" @click=${props.onClose}>Close</button>` : nothing
        }
      </div>
      ${
        cmp.ids.length === 0
          ? html`
              <div class="muted">No matching instances found.</div>
            `
          : renderDiffTable(cmp)
      }
    </section>
  `;
}

function renderDiffTable(cmp: InstanceComparison) {
  return html`
    <table class="diff-table" style="margin-top: 12px;">
      <thead>
        <tr>
          <th>Path</th>
          ${cmp.ids.map((id) => html`<th>${id}</th>`)}
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        ${cmp.differences.map(
          (diff) => html`
            <tr>
              <td><code>${diff.path}</code></td>
              ${cmp.ids.map((id) => html`<td>${renderCell(diff.values[id])}</td>`)}
              <td class="muted">${diff.rationale ?? ""}</td>
            </tr>
          `,
        )}
        ${
          cmp.differences.length === 0
            ? html`<tr>
              <td colspan=${cmp.ids.length + 2} class="muted">No differences.</td>
            </tr>`
            : nothing
        }
      </tbody>
    </table>
  `;
}

function renderCell(v: unknown) {
  if (v === undefined || v === null) {
    return html`
      <span class="chip chip-missing">—</span>
    `;
  }
  if (typeof v === "boolean") {
    return v
      ? html`
          <span class="chip chip-ok">✓</span>
        `
      : html`
          <span class="chip chip-diff">✗</span>
        `;
  }
  if (Array.isArray(v)) {
    if (v.length === 0)
      return html`
        <span class="muted">(empty)</span>
      `;
    return html`
      <div class="chip-row">
        ${v.map((x) => html`<span class="chip">${String(x)}</span>`)}
      </div>
    `;
  }
  if (typeof v === "string") {
    const display = v.length > 60 ? `${v.slice(0, 57)}…` : v;
    return html`<code>${display}</code>`;
  }
  return html`<code>${JSON.stringify(v)}</code>`;
}
